/**
 * React Query hooks for Compliance > CoAs > Missing CoAs (Phase 2).
 *
 * - useMissingCoas(filters)     GET /v1/compliance/coas/missing (offset paginated)
 * - useMissingCoaFilters()      GET /v1/compliance/coas/missing/filters
 * - useWaiveExpectedPo()        POST /v1/compliance/coas/missing/{id}/waive (un-waive: DELETE)
 * - downloadMissingCoasXlsx()   GET /v1/compliance/coas/missing/export
 *
 * Surfaces the set of purchase orders that are expected to have a CoA but do
 * not, so FSQA can chase the supplier. Gated by the compliance.coas access
 * slug (grid / export / waive). Mirrors useCoas.
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPost } from './api';
import { API_BASE } from './apiBase';
import type { FilterOption } from './useCoas';

export const MISSING_API_BASE = `${API_BASE}/v1/compliance/coas/missing`;

export type ExpectedPoStatus = 'missing' | 'not_missing' | 'waived';

export type ExpectedPoRow = {
  id: number;
  po_number: string;
  po_normalized: string;
  supplier_name: string | null;
  vendor_no: string | null;
  po_date: string | null;
  last_receipt_date: string | null;
  days_overdue: number | null;
  status: string;
  matched_doc_count: number;
  item_codes: string | null;
  waived_by: string | null;
  waived_at: string | null;
  waive_reason: string | null;
};

export type ListMissingCoasResponse = {
  rows: ExpectedPoRow[];
  total_count: number;
  limit: number;
  offset: number;
};

// A certificate document related to an expected PO (filed or unmatched), shown
// on the missing-PO detail page so FSQA can jump to the matching cert.
export type RelatedCert = {
  document_id: string;
  file_name: string;
  doc_type: string;
  status: string;
  received_at: string | null;
  sharepoint_web_url: string | null;
};

export type MissingCoaDetail = {
  id: number;
  po_number: string;
  po_normalized: string;
  supplier_name: string | null;
  vendor_no: string | null;
  po_date: string | null;
  last_receipt_date: string | null;
  days_overdue: number | null;
  status: string;
  matched_doc_count: number;
  item_codes: string | null;
  item_codes_list: string[];
  waived_by: string | null;
  waived_at: string | null;
  waive_reason: string | null;
  related_certs: RelatedCert[];
};

export type MissingCoaFilters = {
  q?: string;
  status?: string;
  supplier?: string;
  overdue_min?: number;
};

export type MissingCoaFilterOptions = {
  statuses: FilterOption[];
  suppliers: FilterOption[];
};

function buildQs(f: MissingCoaFilters, offset: number): string {
  const p = new URLSearchParams();
  if (f.q) p.set('q', f.q);
  if (f.status) p.set('status', f.status);
  if (f.supplier) p.set('supplier', f.supplier);
  if (f.overdue_min != null) p.set('overdue_min', String(f.overdue_min));
  p.set('limit', '50');
  if (offset) p.set('offset', String(offset));
  return p.toString();
}

export function useMissingCoas(filters: MissingCoaFilters = {}) {
  const query = useInfiniteQuery<ListMissingCoasResponse, Error>({
    queryKey: ['compliance-missing-coas', filters],
    queryFn: ({ pageParam }) =>
      apiGet<ListMissingCoasResponse>(`${MISSING_API_BASE}?${buildQs(filters, (pageParam as number) ?? 0)}`),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((n, p) => n + p.rows.length, 0);
      return loaded < lastPage.total_count ? loaded : undefined;
    },
    staleTime: 15_000,
    retry: false,
  });
  const rows: ExpectedPoRow[] = (query.data?.pages ?? []).flatMap((p) => p.rows);
  return {
    rows,
    totalCount: query.data?.pages[0]?.total_count ?? null,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage ?? false,
    fetchNextPage: () => query.fetchNextPage(),
    error: query.error,
    refetch: () => query.refetch(),
  };
}

export function useMissingCoaFilters() {
  const query = useQuery<MissingCoaFilterOptions, Error>({
    queryKey: ['compliance-missing-coas-filters'],
    queryFn: () => apiGet<MissingCoaFilterOptions>(`${MISSING_API_BASE}/filters`),
    staleTime: 5 * 60_000,
    retry: false,
  });
  return { options: query.data, isLoading: query.isLoading, error: query.error };
}

export function useMissingCoaDetail(id: string | number | null | undefined) {
  return useQuery<MissingCoaDetail, Error>({
    queryKey: ['compliance-missing-coa', String(id)],
    queryFn: () => apiGet<MissingCoaDetail>(`${MISSING_API_BASE}/${encodeURIComponent(String(id))}`),
    enabled: id != null && id !== '',
    retry: false,
  });
}

// Waive (or un-waive) an expected PO. Waiving marks the PO not_applicable so it
// stops re-appearing on every reconcile; un-waiving clears it back to missing.
// Both invalidate the list and filter queries so the grid reflects the change.
export function useWaiveExpectedPo() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { id: number; reason: string; waive: boolean }>({
    mutationFn: ({ id, reason, waive }) =>
      waive
        ? apiPost(`${MISSING_API_BASE}/${id}/waive`, { reason })
        : apiDelete(`${MISSING_API_BASE}/${id}/waive`),
    onSuccess: (_d, { id }) => {
      void qc.invalidateQueries({ queryKey: ['compliance-missing-coas'] });
      void qc.invalidateQueries({ queryKey: ['compliance-missing-coas-filters'] });
      void qc.invalidateQueries({ queryKey: ['compliance-missing-coa', String(id)] });
    },
  });
}

export async function downloadMissingCoasXlsx(filters: MissingCoaFilters): Promise<void> {
  const qs = buildQs(filters, 0);
  const res = await fetch(`${MISSING_API_BASE}/export${qs ? `?${qs}` : ''}`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Export failed: ${res.status} ${text}`);
  }
  const blob = await res.blob();
  const dispo = res.headers.get('content-disposition') ?? '';
  const m = dispo.match(/filename="([^"]+)"/);
  const filename = m?.[1] ?? 'missing-coas.xlsx';
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

export function missingStatusLabel(s: string): string {
  switch (s) {
    case 'missing': return 'Missing';
    case 'not_missing': return 'Not missing';
    case 'waived': return 'Waived';
    default: return s;
  }
}
